import React, { useState } from 'react';
import { Avatar, Box, Typography, Button, CircularProgress, Fab, Paper, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Add, AddPhotoAlternate, Place, ScubaDiving } from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import InviteButton from './InviteButton';
import SignInNudge from './SignInNudge';
import PostCard from './PostCard';
import PostComposer from './PostComposer';
import { ACCEPTED_MEDIA_TYPES } from '../utils/media';
import CheckInDialog from './CheckInDialog';
import StoriesStrip from './StoriesStrip';
import DiscoverClubsPanel from './DiscoverClubsPanel';
import LeaderboardPanel from './LeaderboardPanel';
import TrendingPostsPanel from './TrendingPostsPanel';
import { useFeed } from '../hooks/useFeed';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';

const FeedPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const { posts, loading, error, addPost, removePost } = useFeed(userId);
  const [composerOpen, setComposerOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [composerMode, setComposerMode] = useState<'text' | 'dive'>('text');
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);
  const openComposer = (mode: 'text' | 'dive') => {
    setComposerMode(mode);
    setComposerOpen(true);
  };
  // The Photo shortcut owns a native file input so the picker opens on the user's own tap
  // (browsers block programmatic .click() from a freshly opened dialog); the chosen files
  // are handed to the composer, which uploads them on open.
  const handlePhotoShortcut = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    setPickedFiles(files);
    openComposer('text');
  };
  const displayName = currentUser?.firstName || '';

  const handleDelete = async (postId: string) => {
    try {
      await removePost(postId);
      showSuccess('Post deleted');
    } catch {
      showError('Failed to delete post');
    }
  };

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="join the community"
          description="Your feed is where your dive buddies and the divers you follow share dives, photos, and plans — sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 640, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Feed</Typography>
        </Box>

        <StoriesStrip currentUserId={userId} currentUserName={displayName} />

        <Paper variant="outlined" sx={{ p: 2, mb: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 700 }}>
              {(displayName[0] || '?').toUpperCase()}
            </Avatar>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => openComposer('text')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openComposer('text'); }}
              sx={{
                flexGrow: 1,
                px: 2,
                py: 1.25,
                borderRadius: 6,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              What's on your mind, diver?
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<ScubaDiving />} onClick={() => openComposer('dive')}>Share a dive</Button>
            <Button size="small" component="label" startIcon={<AddPhotoAlternate />}>
              Photo
              <input type="file" hidden multiple accept={ACCEPTED_MEDIA_TYPES} onChange={handlePhotoShortcut} />
            </Button>
            <Button size="small" startIcon={<Place />} onClick={() => setCheckInOpen(true)}>Check in</Button>
          </Stack>
        </Paper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        <Stack spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
          <TrendingPostsPanel />
          <LeaderboardPanel userId={userId} />
          <DiscoverClubsPanel />
        </Stack>

        {!loading && posts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No posts yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Posts from your dive buddies and the people you follow will show up here.
            </Typography>
            <Box sx={{ mt: 2 }}><InviteButton variant="contained" /></Box>
          </Box>
        )}

        {posts.map(post => (
          <PostCard key={post.postId} post={post} currentUserId={userId} onDelete={handleDelete} />
        ))}

        {isMobile && (
          <Fab
            variant="extended"
            color="primary"
            onClick={() => openComposer('text')}
            sx={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: 1250,
              fontWeight: 700,
            }}
          >
            <Add sx={{ mr: 1 }} />
            New Post
          </Fab>
        )}

        {checkInOpen && <CheckInDialog open onClose={() => setCheckInOpen(false)} onCheckedIn={addPost} />}

        <PostComposer
          key={composerMode}
          initialMode={composerMode}
          initialFiles={pickedFiles}
          open={composerOpen}
          onClose={() => { setComposerOpen(false); setPickedFiles([]); }}
          onCreated={addPost}
        />
      </Box>
    </Box>
  );
};

export default FeedPage;
